import { useEffect, useState, createContext, useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Button,
  Card,
  Input,
  Select,
  Form,
  Tabs,
  ColorPicker,
  InputNumber,
  Switch,
  Space,
  Typography,
  Divider,
  Row,
  Col,
  Radio,
  Checkbox,
  DatePicker,
  Slider,
  Popover,
  message,
  Progress,
} from 'antd'
import {
  PlusOutlined,
  DeleteOutlined,
  EyeOutlined,
  EditOutlined,
  SettingOutlined,
  BgColorsOutlined,
  FontSizeOutlined,
  AppstoreOutlined,
  FormOutlined,
} from '@ant-design/icons'
import * as FaIcons from 'react-icons/fa'
import * as MdIcons from 'react-icons/md'
import * as AiIcons from 'react-icons/ai'
// import * as BiIcons from 'react-icons/bi'
// import * as BsIcons from 'react-icons/bs'
// import * as HiIcons from 'react-icons/hi'
// import * as IoIcons from 'react-icons/io5'
// import * as RiIcons from 'react-icons/ri'
import { useAppServices } from 'hook/services'
import { useParams } from 'react-router-dom'
import { Eye, Loader, Save, Settings } from 'lucide-react'
import FormPreview, { FormPreviewContext } from 'layouts/Pages/FormComponents/FormPreview'

const { Title, Text } = Typography
const { TextArea } = Input
const { Option } = Select
const { TabPane } = Tabs

// Icon libraries for selection
const iconLibraries = {
  FontAwesome: FaIcons,
  Material: MdIcons,
  AntDesign: AiIcons,
  // BoxIcons: BiIcons,
  // Bootstrap: BsIcons,
  // HeroIcons: HiIcons,
  // Ionicons: IoIcons,
  // RemixIcon: RiIcons,
}

// Create Context
const FormBuilderContext = createContext()

export default function StepperFormBuilder() {
  const Service = useAppServices()
  const { company_id, app_id, id } = useParams()
  const params = useParams()
  const navigate = useNavigate()
  const [mode, setMode] = useState('build')
  const [selectedStep, setSelectedStep] = useState(1)
  const [selectedField, setSelectedField] = useState(null)
  const [currentPreviewStep, setCurrentPreviewStep] = useState(1)
  const [formData, setFormData] = useState({})
  const [iconPickerVisible, setIconPickerVisible] = useState(false)
  const [selectedIconLibrary, setSelectedIconLibrary] = useState('FontAwesome')
  const [customFields, setCustomFields] = useState([])
  const [loading, setLoading] = useState(false)
  const [processing, setProcessing] = useState(false)
  const [formLocation, setFormLocation] = useState('')
  const [formConfig, setFormConfig] = useState({
    // Form Settings
    title: 'Multi-Step Form',
    description: 'Complete all steps to submit your information',

    // Colors
    primaryColor: '#3b82f6',
    secondaryColor: '#6b7280',
    fontColor: '#374151',
    backgroundColor: '#f8fafc',
    cardBackgroundColor: '#ffffff',
    completedColor: '#10b981',
    borderColor: '#e5e7eb',
    buttonBackgroundColor: '#3b82f6',
    buttonTextColor: '#ffffff',
    buttonHoverColor: '#2563eb',

    // Step Colors
    stepBackgroundColor: '#1e293b',
    stepTitleColor: '#ffffff',
    stepDescriptionColor: '#cbd5e1',
    stepIconBackground: '#3b82f6',
    stepNumberColor: '#ffffff',

    // Typography
    fontFamily: 'Inter, sans-serif',
    titleFontSize: 24,
    bodyFontSize: 14,
    labelFontSize: 14,
    buttonFontSize: 14,

    // Layout & Spacing
    stepSpacing: 24,
    stepPortionWidth: 100,
    iconSize: 20,
    borderRadius: 8,
    borderWidth: 1,
    buttonBorderRadius: 6,
    cardPadding: 24,
    fieldSpacing: 16,

    // Button Styles
    buttonPadding: 12,
    buttonBorderWidth: 1,
    buttonBorderColor: '#3b82f6',

    steps: [
      {
        id: 1,
        title: 'Personal Information',
        description: 'Tell us about yourself',
        icon: 'FaUser',
        fields: [
          {
            id: '1-1',
            type: 'text',
            customValue: '',
            label: 'Full Name',
            placeholder: 'Enter your full name',
            required: true,
          },
          {
            id: '1-2',
            type: 'email',
            customValue: '',
            label: 'Email Address',
            placeholder: 'Enter your email',
            required: true,
          },
        ],
      },
      {
        id: 2,
        title: 'Contact Details',
        description: 'How can we reach you?',
        icon: 'FaPhone',
        fields: [
          {
            id: '2-1',
            type: 'text',
            customValue: '',
            label: 'Phone Number',
            placeholder: 'Enter your phone number',
            required: false,
          },
          {
            id: '2-2',
            type: 'text',
            customValue: '',
            label: 'Address',
            placeholder: 'Enter your address',
            required: false,
          },
        ],
      },
    ],
  })

  useEffect(() => {
    // Suppress ResizeObserver error
    const originalError = console.error
    console.error = (...args) => {
      if (typeof args[0] === 'string' && args[0].includes('ResizeObserver')) {
        return
      }
      originalError.apply(console, args)
    }

    return () => {
      console.error = originalError
    }
  }, [])

  // Helper functions
  const getCustomValues = async (locationId) => {
    const { response } = await Service.servey.config_data({
      query: `locationId=${locationId}&company_id=${params.company_id}&app_id=${params.app_id}`,
    })
    if (response) {
      const customFields = response.data.map((field) => {
        return {
          key: field.fieldKey,
          label: field.name,
          description: field.description,
          value: field.id,
          ...field,
        }
      })
      setCustomFields(customFields)
    }
    return
  }

  const getSurvey = async () => {
    setLoading(true)
    const { response } = await Service.servey.get_levelupstore({
      query: `form_id=${params.id}`,
    })
    console.log('responseresponseresponseresponse: ', response?.data)
    if (response) {
      await getCustomValues(response.data?.location_id)
      setFormLocation(response.data?.location_id)
      // If you want to load saved form config, set it here
      setFormConfig(response.data.design)
    }
    setLoading(false)
  }

  useEffect(() => {
    getSurvey()
  }, [])

  const toggleMode = () => {
    setMode(mode === 'build' ? 'preview' : 'build')
    setCurrentPreviewStep(1)
    setFormData({})
  }

  const exportConfig = async () => {
    setProcessing(true)
    const payload = {
      design: formConfig,
      name: formConfig?.title,
      company_id,
      app_id,
      location_id: formLocation,
    }

    if (id) {
      payload.form_id = id
      payload.url = `https://snapshotonboarding.levelupmarketplace.io/levelupstore/${company_id}/${app_id}/${formLocation}/${id}/preview`
    } else {
      payload.url = `https://snapshotonboarding.levelupmarketplace.io/levelupstore/${company_id}/${app_id}/${formLocation}/${id}/preview`
    }

    try {
      const { response } = await Service.servey.levelupstore({ payload })
      message.success('Design Saved Successfully')
    } catch (error) {
      message.error('Error saving design')
    }
    setProcessing(false)
  }

  const contextValue = {
    formConfig,
    setFormConfig,
    selectedStep,
    setSelectedStep,
    selectedField,
    setSelectedField,
    formData,
    setFormData,
    currentPreviewStep,
    setCurrentPreviewStep,
    iconPickerVisible,
    setIconPickerVisible,
    selectedIconLibrary,
    setSelectedIconLibrary,
    customFields,
    iconLibraries,
  }
  const handleBack = () => {
    navigate(-1) // navigates to the previous page
  }
  return (
    <>
      {loading ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
            height: '100vh',
            width: '100%',
          }}
        >
          <Loader size={50} />
        </div>
      ) : (
        <FormBuilderContext.Provider value={contextValue}>
          <div className="w-full max-w-full mx-auto p-5 bg-gray-50 min-h-screen">
            {mode === 'build' && (
              <Button onClick={handleBack} className="mb-5">
                ← Back
              </Button>
            )}

            <div
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              className="mb-5"
            >
              <h1 className="text-2xl font-bold text-gray-800">
                {mode === 'build' ? 'Form Builder' : 'Form Preview'}
              </h1>
              <div className="flex gap-2">
                <Button onClick={toggleMode}>
                  {mode === 'build' ? <Eye size={16} /> : <Settings size={16} />}
                  {mode === 'build' ? 'Preview' : 'Back to Edit'}
                </Button>

                {mode === 'build' && (
                  <Button onClick={exportConfig} disabled={processing}>
                    <Save size={16} />
                    {processing ? 'Saving...' : 'Save'}
                  </Button>
                )}
              </div>
            </div>

            <div className="flex-1 max-h-screen">
              {mode === 'build' ? (
                <FormBuilder />
              ) : (
                <FormPreview
                  formConfig={formConfig}
                  mode="preview"
                  showHeader={true}
                  maxHeight="100vh"
                />
              )}
            </div>
          </div>
        </FormBuilderContext.Provider>
      )}
    </>
  )
}

// Form Builder Mode Component
function FormBuilder() {
  const {
    formConfig,
    setFormConfig,
    formData,
    setFormData,
    selectedStep,
    setSelectedStep,
    selectedField,
    setSelectedField,
    iconPickerVisible,
    setIconPickerVisible,
    selectedIconLibrary,
    setSelectedIconLibrary,
    customFields,
    iconLibraries,
  } = useContext(FormBuilderContext)
  console.log('formConfig: ', formConfig)
  console.log('customFieldscustomFieldscustomFields: ', customFields)
  const renderFormField = (field: any) => {
    const commonStyle = {
      fontFamily: formConfig.fontFamily,
      fontSize: formConfig.bodyFontSize,
      borderRadius: formConfig.borderRadius,
    }

    switch (field.type) {
      case 'textarea':
        return (
          <TextArea
            placeholder={field.placeholder}
            value={formData[field.id] || ''}
            onChange={(e) => setFormData((prev) => ({ ...prev, [field.id]: e.target.value }))}
            style={commonStyle}
            rows={4}
          />
        )
      case 'select':
        return (
          <Select
            placeholder={field.placeholder}
            value={formData[field.id]}
            onChange={(value) => setFormData((prev) => ({ ...prev, [field.id]: value }))}
            style={{ ...commonStyle, width: '100%' }}
          >
            {field.options?.map((option: string) => (
              <Option key={option} value={option}>
                {option}
              </Option>
            ))}
          </Select>
        )
      case 'radio':
        return (
          <Radio.Group
            value={formData[field.id]}
            onChange={(e) => setFormData((prev) => ({ ...prev, [field.id]: e.target.value }))}
          >
            <Space direction="vertical">
              {field.options?.map((option: string) => (
                <Radio key={option} value={option}>
                  {option}
                </Radio>
              ))}
            </Space>
          </Radio.Group>
        )
      case 'checkbox':
        return (
          <Checkbox.Group
            value={formData[field.id] || []}
            onChange={(values) => setFormData((prev) => ({ ...prev, [field.id]: values }))}
          >
            <Space direction="vertical">
              {field.options?.map((option: string) => (
                <Checkbox key={option} value={option}>
                  {option}
                </Checkbox>
              ))}
            </Space>
          </Checkbox.Group>
        )
      case 'date':
        return (
          <DatePicker
            placeholder={field.placeholder}
            value={formData[field.id]}
            onChange={(date) => setFormData((prev) => ({ ...prev, [field.id]: date }))}
            style={{ ...commonStyle, width: '100%' }}
          />
        )
      case 'number':
        return (
          <InputNumber
            placeholder={field.placeholder}
            value={formData[field.id]}
            onChange={(value) => setFormData((prev) => ({ ...prev, [field.id]: value }))}
            style={{ ...commonStyle, width: '100%' }}
          />
        )
      default:
        return (
          <Input
            type={field.type}
            placeholder={field.placeholder}
            value={formData[field.id] || ''}
            onChange={(e) => setFormData((prev) => ({ ...prev, [field.id]: e.target.value }))}
            style={commonStyle}
          />
        )
    }
  }

  // Helper functions
  const updateFormConfig = (updates) => {
    setFormConfig((prev) => ({ ...prev, ...updates }))
  }

  const updateStep = (stepId, updates) => {
    setFormConfig((prev) => ({
      ...prev,
      steps: prev?.steps?.map((step) => (step.id === stepId ? { ...step, ...updates } : step)),
    }))
  }

  const addStep = () => {
    const newId = Math.max(...formConfig?.steps?.map((s) => s.id)) + 1
    const newStep = {
      id: newId,
      title: `Step ${newId}`,
      description: 'Step description',
      icon: 'FaCircle',
      fields: [],
    }
    setFormConfig((prev) => ({
      ...prev,
      steps: [...prev?.steps, newStep],
    }))
    setSelectedStep(newId)
  }

  const deleteStep = (stepId) => {
    if (formConfig?.steps?.length <= 1) return
    setFormConfig((prev) => ({
      ...prev,
      steps: prev?.steps?.filter((step) => step.id !== stepId),
    }))
    if (selectedStep === stepId) {
      setSelectedStep(formConfig?.steps[0].id)
    }
  }

  const addField = () => {
    const stepIndex = formConfig?.steps?.findIndex((s) => s.id === selectedStep)
    if (stepIndex === -1) return

    const newFieldId = `${selectedStep}-${Date.now()}`
    const newField = {
      id: newFieldId,
      type: 'text',
      label: 'New Field',
      customValue: '',
      placeholder: 'Enter value',
      required: false,
    }

    const updatedSteps = [...formConfig?.steps]
    updatedSteps[stepIndex] = {
      ...updatedSteps[stepIndex],
      fields: [...updatedSteps[stepIndex].fields, newField],
    }

    setFormConfig((prev) => ({ ...prev, steps: updatedSteps }))
    setSelectedField(newFieldId)
  }

  const updateField = (fieldId: string, updates: any) => {
    console.log('fieldId: ', fieldId)
    console.log('updates: ', updates)
    setFormConfig((prev) => ({
      ...prev,
      steps: prev?.steps?.map((step) => ({
        ...step,
        fields: step?.fields?.map((field) =>
          field.id === fieldId ? { ...field, ...updates } : field
        ),
      })),
    }))
  }

  const deleteField = (fieldId) => {
    setFormConfig((prev) => ({
      ...prev,
      steps: prev?.steps?.map((step) => ({
        ...step,
        fields: step?.fields?.filter((field) => field.id !== fieldId),
      })),
    }))
    if (selectedField === fieldId) {
      setSelectedField(null)
    }
  }

  const getCurrentStep = () => {
    return formConfig?.steps?.find((step) => step.id === selectedStep)
  }

  const getCurrentField = () => {
    const step = getCurrentStep()
    return step?.fields.find((field) => field.id === selectedField)
  }
  const currentField = getCurrentField

  const renderIcon = (iconName, size = 20) => {
    const IconComponent = iconLibraries[selectedIconLibrary]?.[iconName] || FaIcons.FaCircle
    return <IconComponent size={size} />
  }

  // Icon Picker Component
  const IconPicker = () => {
    const icons = Object.keys(iconLibraries[selectedIconLibrary])
      .filter((name) => name.startsWith('Fa') || name.startsWith('Md') || name.startsWith('Ai'))
      .slice(0, 50)
    return (
      <div style={{ width: 400, maxHeight: 300, overflow: 'auto' }}>
        <Select
          value={selectedIconLibrary}
          onChange={setSelectedIconLibrary}
          style={{ width: '100%', marginBottom: 16 }}
        >
          {Object.keys(iconLibraries).map((lib) => (
            <Option key={lib} value={lib}>
              {lib}
            </Option>
          ))}
        </Select>
        <Row gutter={[8, 8]}>
          {icons.map((iconName) => {
            const IconComponent = iconLibraries[selectedIconLibrary][iconName]
            return (
              <Col span={4} key={iconName}>
                <Button
                  type="text"
                  style={{
                    width: '100%',
                    height: 40,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  onClick={() => {
                    updateStep(selectedStep, { icon: iconName })
                    setIconPickerVisible(false)
                  }}
                >
                  <IconComponent size={20} />
                </Button>
              </Col>
            )
          })}
        </Row>
      </div>
    )
  }

  // Vertical Stepper Component matching the image design
  const VerticalStepper = ({ steps, current, onStepClick }) => {
    return (
      <div style={{ padding: '24px 0' }}>
        {steps?.map((step, index) => {
          const isActive = step.id === current
          const isCompleted = index < steps?.findIndex((s) => s.id === current)

          return (
            <div
              key={step.id}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                marginBottom: index < steps?.length - 1 ? formConfig?.stepspacing : 0,
                cursor: 'pointer',
                opacity: isActive ? 1 : 0.7,
              }}
              onClick={() => onStepClick?.(step.id)}
            >
              {/* Icon Circle */}
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  backgroundColor: isCompleted
                    ? formConfig.completedColor
                    : isActive
                    ? formConfig.primaryColor
                    : formConfig.secondaryColor,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#ffffff',
                  marginRight: 16,
                  flexShrink: 0,
                }}
              >
                {renderIcon(step.icon, formConfig.iconSize)}
              </div>

              {/* Content */}
              <div style={{ flex: 1, paddingTop: 4 }}>
                <div
                  style={{
                    fontSize: formConfig.bodyFontSize + 2,
                    fontWeight: isActive ? 600 : 400,
                    color: isActive ? formConfig.stepTitleColor : '#cbd5e1',
                    marginBottom: 4,
                    fontFamily: formConfig.fontFamily,
                  }}
                >
                  {step.title}
                </div>
                <div
                  style={{
                    fontSize: formConfig.bodyFontSize - 2,
                    color: formConfig.stepDescriptionColor,
                    fontFamily: formConfig.fontFamily,
                  }}
                >
                  {step.description}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      <div style={{ padding: 24 }}>
        <Row gutter={24}>
          {/* Left Panel - Steps List */}
          <Col span={6}>
            <Card
              title={
                <div
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <span>Steps</span>
                  <Button type="primary" size="small" icon={<PlusOutlined />} onClick={addStep}>
                    Add
                  </Button>
                </div>
              }
              size="small"
            >
              <div style={{ maxHeight: 400, overflow: 'auto' }}>
                {formConfig?.steps?.map((step, index) => (
                  <Card
                    key={step.id}
                    size="small"
                    style={{
                      marginBottom: 8,
                      cursor: 'pointer',
                      border: selectedStep === step.id ? '2px solid #1890ff' : '1px solid #f0f0f0',
                    }}
                    onClick={() => {
                      setSelectedStep(step.id)
                      setSelectedField(null)
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span
                            style={{
                              backgroundColor: formConfig.stepIconBackground,
                              borderRadius: '50%',
                              width: 20,
                              height: 20,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: 12,
                              color: formConfig.stepNumberColor,
                            }}
                          >
                            {index + 1}
                          </span>
                          <Text strong>{step.title}</Text>
                        </div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {step.description}
                        </Text>
                        <div style={{ marginTop: 4 }}>
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            {step?.fields?.length} fields • Icon: {step.icon}
                          </Text>
                        </div>
                      </div>
                      {formConfig?.steps?.length > 1 && (
                        <Button
                          type="text"
                          size="small"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={(e) => {
                            e.stopPropagation()
                            deleteStep(step.id)
                          }}
                        />
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            </Card>

            {/* Fields List */}
            {selectedStep && (
              <Card
                title={
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <span>Fields</span>
                    <Button type="primary" size="small" icon={<PlusOutlined />} onClick={addField}>
                      Add
                    </Button>
                  </div>
                }
                size="small"
                style={{ marginTop: 16 }}
              >
                <div style={{ maxHeight: 300, overflow: 'auto' }}>
                  {getCurrentStep()?.fields.map((field) => (
                    <Card
                      key={field.id}
                      size="small"
                      style={{
                        marginBottom: 8,
                        cursor: 'pointer',
                        border:
                          selectedField === field.id ? '2px solid #1890ff' : '1px solid #f0f0f0',
                      }}
                      onClick={() => setSelectedField(field.id)}
                    >
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <div>
                          <Text strong>{field.label}</Text>
                          <div>
                            <Text type="secondary" style={{ fontSize: 11 }}>
                              {field.type} {field.required && '• Required'}
                            </Text>
                          </div>
                        </div>
                        <Button
                          type="text"
                          size="small"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={(e) => {
                            e.stopPropagation()
                            deleteField(field.id)
                          }}
                        />
                      </div>
                    </Card>
                  ))}
                </div>
              </Card>
            )}
          </Col>

          {/* Middle Panel - Form Preview */}
          <Col span={12}>
            <Card title="Form Preview" size="small">
              <div
                style={{
                  backgroundColor: formConfig.cardBackgroundColor || 'grey',
                  borderRadius: formConfig.borderRadius,
                  border: `${formConfig.borderWidth}px solid ${formConfig.borderColor}`,
                  padding: formConfig.cardPadding,
                  fontFamily: formConfig.fontFamily,
                  minHeight: 500,
                }}
              >
                <div style={{ textAlign: 'center', marginBottom: 32 }}>
                  <Title
                    level={2}
                    style={{
                      color: formConfig.primaryColor,
                      fontSize: formConfig.titleFontSize,
                      fontFamily: formConfig.fontFamily,
                    }}
                  >
                    {formConfig.title}
                  </Title>
                  <Text
                    style={{
                      color: formConfig.fontColor,
                      fontSize: formConfig.bodyFontSize,
                      fontFamily: formConfig.fontFamily,
                    }}
                  >
                    {formConfig.description}
                  </Text>
                </div>

                {/* Mini Vertical Stepper Preview */}
                <div
                  style={{
                    backgroundColor: formConfig.stepBackgroundColor,
                    borderRadius: formConfig.borderRadius,
                    padding: 16,
                    marginBottom: 24,
                  }}
                >
                  <VerticalStepper
                    steps={formConfig?.steps}
                    current={selectedStep}
                    onStepClick={setSelectedStep}
                  />
                </div>

                {/* Current Step Fields Preview */}
                {getCurrentStep() && (
                  <div>
                    <Title
                      level={4}
                      style={{
                        color: formConfig.fontColor,
                        fontFamily: formConfig.fontFamily,
                      }}
                    >
                      {getCurrentStep()?.title}
                    </Title>
                    <Form layout="vertical">
                      {getCurrentStep()?.fields.map((field) => (
                        <Form.Item
                          key={field.id}
                          label={
                            <span
                              style={{
                                fontSize: formConfig.labelFontSize,
                                color: formConfig.fontColor,
                                fontFamily: formConfig.fontFamily,
                              }}
                            >
                              {field.label}
                              {field.required && <span style={{ color: '#ff4d4f' }}> *</span>}
                            </span>
                          }
                          style={{ marginBottom: formConfig.fieldSpacing }}
                        >
                          {renderFormField(field)}
                        </Form.Item>
                      ))}
                    </Form>
                  </div>
                )}
              </div>
            </Card>
          </Col>

          {/* Right Panel - Settings */}
          <Col span={6}>
            <Card title="Settings" size="small">
              <Tabs defaultActiveKey="styling" size="small">
                <TabPane
                  tab={
                    <span>
                      <BgColorsOutlined /> Styling
                    </span>
                  }
                  key="styling"
                >
                  <div style={{ maxHeight: 600, overflow: 'auto' }}>
                    <Title level={5}>Form Colors</Title>
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <div>
                        <Text>Primary Color</Text>
                        <ColorPicker
                          value={formConfig.primaryColor}
                          onChange={(color) =>
                            updateFormConfig({ primaryColor: color.toHexString() })
                          }
                          style={{ width: '100%' }}
                        />
                      </div>
                      <div>
                        <Text>Secondary Color</Text>
                        <ColorPicker
                          value={formConfig.secondaryColor}
                          onChange={(color) =>
                            updateFormConfig({ secondaryColor: color.toHexString() })
                          }
                          style={{ width: '100%' }}
                        />
                      </div>
                      <div>
                        <Text>Font Color</Text>
                        <ColorPicker
                          value={formConfig.fontColor}
                          onChange={(color) => updateFormConfig({ fontColor: color.toHexString() })}
                          style={{ width: '100%' }}
                        />
                      </div>
                      <div>
                        <Text>Background Color</Text>
                        <ColorPicker
                          value={formConfig.backgroundColor}
                          onChange={(color) =>
                            updateFormConfig({ backgroundColor: color.toHexString() })
                          }
                          style={{ width: '100%' }}
                        />
                      </div>
                      <div>
                        <Text>Card Background</Text>
                        <ColorPicker
                          value={formConfig.cardBackgroundColor}
                          onChange={(color) =>
                            updateFormConfig({ cardBackgroundColor: color.toHexString() })
                          }
                          style={{ width: '100%' }}
                        />
                      </div>
                      <div>
                        <Text>Completed Color</Text>
                        <ColorPicker
                          value={formConfig.completedColor}
                          onChange={(color) =>
                            updateFormConfig({ completedColor: color.toHexString() })
                          }
                          style={{ width: '100%' }}
                        />
                      </div>
                      <div>
                        <Text>Border Color</Text>
                        <ColorPicker
                          value={formConfig.borderColor}
                          onChange={(color) =>
                            updateFormConfig({ borderColor: color.toHexString() })
                          }
                          style={{ width: '100%' }}
                        />
                      </div>
                    </Space>

                    <Divider />
                    <Title level={5}>Step Colors</Title>
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <div>
                        <Text>Step Background</Text>
                        <ColorPicker
                          value={formConfig.stepBackgroundColor}
                          onChange={(color) =>
                            updateFormConfig({ stepBackgroundColor: color.toHexString() })
                          }
                          style={{ width: '100%' }}
                        />
                      </div>
                      <div>
                        <Text>Step Title Color</Text>
                        <ColorPicker
                          value={formConfig.stepTitleColor}
                          onChange={(color) =>
                            updateFormConfig({ stepTitleColor: color.toHexString() })
                          }
                          style={{ width: '100%' }}
                        />
                      </div>
                      <div>
                        <Text>Step Description Color</Text>
                        <ColorPicker
                          value={formConfig.stepDescriptionColor}
                          onChange={(color) =>
                            updateFormConfig({ stepDescriptionColor: color.toHexString() })
                          }
                          style={{ width: '100%' }}
                        />
                      </div>
                      <div>
                        <Text>Step Icon Background</Text>
                        <ColorPicker
                          value={formConfig.stepIconBackground}
                          onChange={(color) =>
                            updateFormConfig({ stepIconBackground: color.toHexString() })
                          }
                          style={{ width: '100%' }}
                        />
                      </div>
                      <div>
                        <Text>Step Number Color</Text>
                        <ColorPicker
                          value={formConfig.stepNumberColor}
                          onChange={(color) =>
                            updateFormConfig({ stepNumberColor: color.toHexString() })
                          }
                          style={{ width: '100%' }}
                        />
                      </div>
                    </Space>

                    <Divider />
                    <Title level={5}>Typography</Title>
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <div>
                        <Text>Font Family</Text>
                        <Select
                          value={formConfig.fontFamily}
                          onChange={(value) => updateFormConfig({ fontFamily: value })}
                          style={{ width: '100%' }}
                        >
                          <Option value="Inter, sans-serif">Inter</Option>
                          <Option value="Roboto, sans-serif">Roboto</Option>
                          <Option value="Open Sans, sans-serif">Open Sans</Option>
                          <Option value="Lato, sans-serif">Lato</Option>
                          <Option value="Montserrat, sans-serif">Montserrat</Option>
                          <Option value="Poppins, sans-serif">Poppins</Option>
                        </Select>
                      </div>
                      <div>
                        <Text>Title Font Size</Text>
                        <InputNumber
                          value={formConfig.titleFontSize}
                          onChange={(value) => updateFormConfig({ titleFontSize: value })}
                          style={{ width: '100%' }}
                          min={12}
                          max={48}
                        />
                      </div>
                      <div>
                        <Text>Body Font Size</Text>
                        <InputNumber
                          value={formConfig.bodyFontSize}
                          onChange={(value) => updateFormConfig({ bodyFontSize: value })}
                          style={{ width: '100%' }}
                          min={10}
                          max={24}
                        />
                      </div>
                      <div>
                        <Text>Label Font Size</Text>
                        <InputNumber
                          value={formConfig.labelFontSize}
                          onChange={(value) => updateFormConfig({ labelFontSize: value })}
                          style={{ width: '100%' }}
                          min={10}
                          max={24}
                        />
                      </div>
                    </Space>

                    <Divider />
                    <Title level={5}>Layout & Spacing</Title>
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <div>
                        <Text>Step Spacing</Text>
                        <Slider
                          value={formConfig?.stepspacing}
                          onChange={(value) => updateFormConfig({ stepSpacing: value })}
                          min={8}
                          max={48}
                        />
                      </div>
                      <div>
                        <Text>Step Portion Width</Text>
                        <Slider
                          value={formConfig.stepPortionWidth}
                          onChange={(value) => updateFormConfig({ stepPortionWidth: value })}
                          min={80}
                          max={200}
                        />
                      </div>
                      <div>
                        <Text>Icon Size</Text>
                        <Slider
                          value={formConfig.iconSize}
                          onChange={(value) => updateFormConfig({ iconSize: value })}
                          min={12}
                          max={32}
                        />
                      </div>
                      <div>
                        <Text>Border Radius</Text>
                        <Slider
                          value={formConfig.borderRadius}
                          onChange={(value) => updateFormConfig({ borderRadius: value })}
                          min={0}
                          max={24}
                        />
                      </div>
                      <div>
                        <Text>Border Width</Text>
                        <Slider
                          value={formConfig.borderWidth}
                          onChange={(value) => updateFormConfig({ borderWidth: value })}
                          min={0}
                          max={8}
                        />
                      </div>
                      <div>
                        <Text>Card Padding</Text>
                        <Slider
                          value={formConfig.cardPadding}
                          onChange={(value) => updateFormConfig({ cardPadding: value })}
                          min={8}
                          max={48}
                        />
                      </div>
                      <div>
                        <Text>Field Spacing</Text>
                        <Slider
                          value={formConfig.fieldSpacing}
                          onChange={(value) => updateFormConfig({ fieldSpacing: value })}
                          min={8}
                          max={32}
                        />
                      </div>
                    </Space>

                    <Divider />
                    <Title level={5}>Button Styles</Title>
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <div>
                        <Text>Button Background</Text>
                        <ColorPicker
                          value={formConfig.buttonBackgroundColor}
                          onChange={(color) =>
                            updateFormConfig({ buttonBackgroundColor: color.toHexString() })
                          }
                          style={{ width: '100%' }}
                        />
                      </div>
                      <div>
                        <Text>Button Text Color</Text>
                        <ColorPicker
                          value={formConfig.buttonTextColor}
                          onChange={(color) =>
                            updateFormConfig({ buttonTextColor: color.toHexString() })
                          }
                          style={{ width: '100%' }}
                        />
                      </div>
                      <div>
                        <Text>Button Hover Color</Text>
                        <ColorPicker
                          value={formConfig.buttonHoverColor}
                          onChange={(color) =>
                            updateFormConfig({ buttonHoverColor: color.toHexString() })
                          }
                          style={{ width: '100%' }}
                        />
                      </div>
                      <div>
                        <Text>Button Border Color</Text>
                        <ColorPicker
                          value={formConfig.buttonBorderColor}
                          onChange={(color) =>
                            updateFormConfig({ buttonBorderColor: color.toHexString() })
                          }
                          style={{ width: '100%' }}
                        />
                      </div>
                      <div>
                        <Text>Button Border Radius</Text>
                        <Slider
                          value={formConfig.buttonBorderRadius}
                          onChange={(value) => updateFormConfig({ buttonBorderRadius: value })}
                          min={0}
                          max={24}
                        />
                      </div>
                      <div>
                        <Text>Button Padding</Text>
                        <Slider
                          value={formConfig.buttonPadding}
                          onChange={(value) => updateFormConfig({ buttonPadding: value })}
                          min={8}
                          max={24}
                        />
                      </div>
                      <div>
                        <Text>Button Border Width</Text>
                        <Slider
                          value={formConfig.buttonBorderWidth}
                          onChange={(value) => updateFormConfig({ buttonBorderWidth: value })}
                          min={0}
                          max={8}
                        />
                      </div>
                    </Space>
                  </div>
                </TabPane>

                <TabPane
                  tab={
                    <span>
                      <SettingOutlined /> Steps
                    </span>
                  }
                  key="steps"
                >
                  {selectedStep && (
                    <div style={{ maxHeight: 600, overflow: 'auto' }}>
                      <Space direction="vertical" style={{ width: '100%' }}>
                        <div>
                          <Text>Form Title</Text>
                          <Input
                            value={formConfig.title}
                            onChange={(e) => updateFormConfig({ title: e.target.value })}
                          />
                        </div>
                        <div>
                          <Text>Form Description</Text>
                          <TextArea
                            value={formConfig.description}
                            onChange={(e) => updateFormConfig({ description: e.target.value })}
                            rows={3}
                          />
                        </div>
                        <Divider />
                        <div>
                          <Text>Step Title</Text>
                          <Input
                            value={getCurrentStep()?.title}
                            onChange={(e) => updateStep(selectedStep, { title: e.target.value })}
                          />
                        </div>
                        <div>
                          <Text>Step Description</Text>
                          <Input
                            value={getCurrentStep()?.description}
                            onChange={(e) =>
                              updateStep(selectedStep, { description: e.target.value })
                            }
                          />
                        </div>
                        <div>
                          <Text>Step Icon</Text>
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <Input
                              value={getCurrentStep()?.icon}
                              onChange={(e) => updateStep(selectedStep, { icon: e.target.value })}
                              style={{ flex: 1 }}
                            />
                            <Popover
                              content={<IconPicker />}
                              title="Select Icon"
                              trigger="click"
                              open={iconPickerVisible}
                              onOpenChange={setIconPickerVisible}
                            >
                              <Button icon={<AppstoreOutlined />}>Pick</Button>
                            </Popover>
                          </div>
                          <div
                            style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}
                          >
                            {renderIcon(getCurrentStep()?.icon, 20)}
                            <Text type="secondary">Current icon preview</Text>
                          </div>
                        </div>
                      </Space>
                    </div>
                  )}
                </TabPane>

                <TabPane
                  tab={
                    <span>
                      <FormOutlined /> Fields
                    </span>
                  }
                  key="fields"
                >
                  {selectedField && (
                    <div style={{ maxHeight: 600, overflow: 'auto' }}>
                      <Space direction="vertical" style={{ width: '100%' }}>
                        <div>
                          <Text>Field Label</Text>
                          <Input
                            value={getCurrentField()?.label}
                            onChange={(e) => updateField(selectedField, { label: e.target.value })}
                          />
                        </div>
                        <div>
                          <Text>Field Type</Text>
                          <Select
                            value={getCurrentField()?.type}
                            onChange={(value) => updateField(selectedField, { type: value })}
                            style={{ width: '100%' }}
                          >
                            <Option value="text">Text</Option>
                            <Option value="email">Email</Option>
                            <Option value="password">Password</Option>
                            <Option value="number">Number</Option>
                            <Option value="tel">Telephone</Option>
                            <Option value="textarea">Text Area</Option>
                            <Option value="select">Dropdown</Option>
                            <Option value="checkbox">Checkbox</Option>
                            <Option value="radio">Radio Buttons</Option>
                            <Option value="date">Date</Option>
                            <Option value="time">Time</Option>
                            <Option value="datetime-local">Date & time</Option>
                            <Option value="color">Color</Option>
                            <Option value="file">File</Option>
                            <Option value="url">URL</Option>
                          </Select>
                        </div>
                        <div>
                          <Text>Placeholder</Text>
                          <Input
                            value={getCurrentField()?.placeholder}
                            onChange={(e) =>
                              updateField(selectedField, { placeholder: e.target.value })
                            }
                          />
                        </div>
                        <div>
                          <Checkbox
                            checked={getCurrentField()?.required}
                            onChange={(e) =>
                              updateField(selectedField, { required: e.target.checked })
                            }
                          >
                            Required Field
                          </Checkbox>
                        </div>
                        {['select', 'radio', 'checkbox'].includes(getCurrentField()?.type) && (
                          <div>
                            <Text>Options (comma separated)</Text>
                            <TextArea
                              value={getCurrentField()?.options?.join(', ') || ''}
                              onChange={(e) => {
                                const options = e.target.value.split(/\s*,\s*/).filter(Boolean)

                                updateField(selectedField, { options })
                              }}
                              rows={4}
                            />
                          </div>
                        )}

                        <div>
                          <Text>Custom Field Value</Text>
                          <Select
                            value={getCurrentField()?.customValue}
                            onChange={(value) => updateField(selectedField, { customValue: value })}
                            style={{ width: '100%' }}
                            allowClear
                            placeholder="Select custom field or leave empty"
                          >
                            {customFields.map((field) => (
                              <Option key={field.id} value={field.id}>
                                {field.label}
                              </Option>
                            ))}
                          </Select>
                        </div>
                      </Space>
                    </div>
                  )}
                </TabPane>
              </Tabs>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  )
}
